import { supabase } from '../lib/supabase'
import type {
  AccountHealthConfig,
  AccountHealthLevel,
  AccountHealthLevelCreate,
  AccountHealthLevelUpdate,
  AccountHealthOption,
  AccountHealthOptionCreate,
  AccountHealthOptionUpdate,
  AccountHealthPillar,
  AccountHealthPillarCreate,
  AccountHealthPillarUpdate,
} from '../types/comercialAccountHealth'

function mapLevel(row: Record<string, unknown>): AccountHealthLevel {
  return {
    id: String(row.id ?? ''),
    label: typeof row.label === 'string' ? row.label : '',
    min_score: typeof row.min_score === 'number' ? row.min_score : Number(row.min_score) || 0,
    max_score: typeof row.max_score === 'number' ? row.max_score : Number(row.max_score) || 0,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : Number(row.sort_order) || 0,
    created_at: typeof row.created_at === 'string' ? row.created_at : null,
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : null,
  }
}

function mapOption(row: Record<string, unknown>): AccountHealthOption {
  return {
    id: String(row.id ?? ''),
    pillar_id: String(row.pillar_id ?? ''),
    label: typeof row.label === 'string' ? row.label : '',
    emoji: typeof row.emoji === 'string' ? row.emoji : null,
    points: typeof row.points === 'number' ? row.points : Number(row.points) || 0,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : Number(row.sort_order) || 0,
    is_active: row.is_active !== false,
    created_at: typeof row.created_at === 'string' ? row.created_at : null,
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : null,
  }
}

function mapPillar(row: Record<string, unknown>, options: AccountHealthOption[]): AccountHealthPillar {
  const id = String(row.id ?? '')
  return {
    id,
    title: typeof row.title === 'string' ? row.title : '',
    question: typeof row.question === 'string' ? row.question : '',
    max_points: typeof row.max_points === 'number' ? row.max_points : Number(row.max_points) || 0,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : Number(row.sort_order) || 0,
    is_active: row.is_active !== false,
    created_at: typeof row.created_at === 'string' ? row.created_at : null,
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : null,
    options: options
      .filter((o) => o.pillar_id === id)
      .sort((a, b) => a.sort_order - b.sort_order),
  }
}

export const comercialAccountHealthService = {
  async listConfig(): Promise<AccountHealthConfig> {
    const [levelsRes, pillarsRes, optionsRes] = await Promise.all([
      supabase
        .from('comercial_account_health_levels')
        .select('*')
        .order('sort_order', { ascending: true }),
      supabase
        .from('comercial_account_health_pillars')
        .select('*')
        .order('sort_order', { ascending: true }),
      supabase
        .from('comercial_account_health_options')
        .select('*')
        .order('sort_order', { ascending: true }),
    ])

    if (levelsRes.error) throw levelsRes.error
    if (pillarsRes.error) throw pillarsRes.error
    if (optionsRes.error) throw optionsRes.error

    const options = (optionsRes.data ?? []).map((row) => mapOption(row as Record<string, unknown>))
    const pillars = (pillarsRes.data ?? []).map((row) =>
      mapPillar(row as Record<string, unknown>, options)
    )
    const levels = (levelsRes.data ?? []).map((row) => mapLevel(row as Record<string, unknown>))

    return { levels, pillars }
  },

  async createLevel(data: AccountHealthLevelCreate): Promise<AccountHealthLevel> {
    const { data: result, error } = await supabase
      .from('comercial_account_health_levels')
      .insert({
        label: data.label,
        min_score: data.min_score,
        max_score: data.max_score,
        sort_order: data.sort_order,
      })
      .select('*')
      .single()

    if (error) throw error
    return mapLevel(result as Record<string, unknown>)
  },

  async updateLevel(id: string, data: AccountHealthLevelUpdate): Promise<AccountHealthLevel> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() }
    if (data.label !== undefined) payload.label = data.label
    if (data.min_score !== undefined) payload.min_score = data.min_score
    if (data.max_score !== undefined) payload.max_score = data.max_score
    if (data.sort_order !== undefined) payload.sort_order = data.sort_order

    const { data: result, error } = await supabase
      .from('comercial_account_health_levels')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single()

    if (error) throw error
    return mapLevel(result as Record<string, unknown>)
  },

  async deleteLevel(id: string): Promise<void> {
    const { error } = await supabase.from('comercial_account_health_levels').delete().eq('id', id)
    if (error) throw error
  },

  async createPillar(data: AccountHealthPillarCreate): Promise<AccountHealthPillar> {
    const { data: result, error } = await supabase
      .from('comercial_account_health_pillars')
      .insert({
        title: data.title,
        question: data.question,
        max_points: data.max_points,
        sort_order: data.sort_order,
        is_active: data.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return mapPillar(result as Record<string, unknown>, [])
  },

  async updatePillar(id: string, data: AccountHealthPillarUpdate): Promise<AccountHealthPillar> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() }
    if (data.title !== undefined) payload.title = data.title
    if (data.question !== undefined) payload.question = data.question
    if (data.max_points !== undefined) payload.max_points = data.max_points
    if (data.is_active !== undefined) payload.is_active = data.is_active

    const { data: result, error } = await supabase
      .from('comercial_account_health_pillars')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single()

    if (error) throw error

    const { data: optionsData, error: optionsError } = await supabase
      .from('comercial_account_health_options')
      .select('*')
      .eq('pillar_id', id)
      .order('sort_order', { ascending: true })

    if (optionsError) throw optionsError
    const options = (optionsData ?? []).map((row) => mapOption(row as Record<string, unknown>))
    return mapPillar(result as Record<string, unknown>, options)
  },

  async deletePillar(id: string): Promise<void> {
    const { error } = await supabase.from('comercial_account_health_pillars').delete().eq('id', id)
    if (error) throw error
  },

  async createOption(data: AccountHealthOptionCreate): Promise<AccountHealthOption> {
    const { data: result, error } = await supabase
      .from('comercial_account_health_options')
      .insert({
        pillar_id: data.pillar_id,
        label: data.label,
        emoji: data.emoji ?? null,
        points: data.points,
        sort_order: data.sort_order,
        is_active: data.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return mapOption(result as Record<string, unknown>)
  },

  async updateOption(id: string, data: AccountHealthOptionUpdate): Promise<AccountHealthOption> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() }
    if (data.label !== undefined) payload.label = data.label
    if (data.emoji !== undefined) payload.emoji = data.emoji
    if (data.points !== undefined) payload.points = data.points
    if (data.is_active !== undefined) payload.is_active = data.is_active

    const { data: result, error } = await supabase
      .from('comercial_account_health_options')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single()

    if (error) throw error
    return mapOption(result as Record<string, unknown>)
  },

  async deleteOption(id: string): Promise<void> {
    const { error } = await supabase.from('comercial_account_health_options').delete().eq('id', id)
    if (error) throw error
  },
}
